import React, { useState } from "react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { pluralKey } from "../lib/pluralKey";
import TenantPicker from "./TenantPicker";
import { tenantDisplayName } from "./tenantDisplay";
import IdCell from "./IdCell";
import KeepCompounds from "../components/KeepCompounds";
import HorizontalScroller from "./HorizontalScroller";
import { DateTimeCell, LedgerAmount, LedgerFigure, PracticeCell } from "./LedgerCells";
import { formatMoneyLedger } from "../lib/formatMoney";
import { KIND_OPTIONS, clientTxKindLabel, isKnownKind, statusChip } from "./clientTxStatus";
import LoadFailure from "../components/ui/LoadFailure";
import Pager from "../components/ui/Pager";
import { usePagedList } from "./usePagedList";
import { useFilteredRead } from "./useFilteredRead";
import { apiErrorCode } from "../lib/apiErrors";
import { Loader2 } from "lucide-react";

const PAGE_SIZE = 25;

const PAYMENT_STATUS_OPTIONS = ["paid", "unpaid"];
const REFUND_STATUS_OPTIONS = ["none", "partial", "refunded", "unverified"];

// The footer is its own read, asked once per filter set and never on a page turn: it totals
// every filtered row, which can cost far more than a page (contract D1 section 4).
const TOTALS_PATH = "/super-admin/client-transactions/totals";
// The server stopped the totals at its time budget: too many rows match these filters.
const TOTALS_TIMEOUT = "CLIENT_TX_TOTALS_TIMEOUT";
// A 200 that is not the footer (the production proxy answers 200 + index.html) is a failed read.
// So is one without the missed-capture count: reading its absence as 0 would hide exactly the
// sales the count exists to report.
const isTotalsPayload = (d) => Boolean(d) && d.totals_by_currency !== null
  && typeof d.totals_by_currency === "object" && !Array.isArray(d.totals_by_currency)
  && Number.isInteger(d.ledger_captured_unpaid) && d.ledger_captured_unpaid >= 0;

// The figure sources whose row carries no figure at all: nothing to explain about a tax.
const NO_FIGURES = new Set(["not_collected", "capture_unbooked", "ledger_unavailable", "ledger_mixed_currency"]);

// Every amount in a row or in the footer is a `LedgerFigure` no wider than this (contract D1
// section 22). An unbreakable figure is as wide as its amount, so the 1280 fit used to hold only
// up to some size of total: 9 digits left the fr table 1px (918px in its 919px box), 10 digits
// made it 960px. Capped, a column's width no longer depends on the amounts: with all four money
// columns and the refunded figure at the cap and every word cap reached, the narrowest fr table
// at 1280 measures 914px of 919px (en 867, es 866; 2026-10-12, sa_d1_clienttx_fit.py's huge rows).
// 11ch holds every figure under ten million on one line in en/fr/es (10.5ch at most); a larger
// one, or a refund of a million or more with its minus, breaks between its digit groups. 11.5ch,
// which would keep eight digits whole, measured 931px in fr.
const FIGURE_CAP = "max-w-[11ch]";

/**
 * Integer cents -> "120.00 CAD", or `null` when the figure itself is unknown.
 *
 * Every derived figure (`collected_cents`, `commission_cents`, `owed_to_practitioner_cents`...)
 * comes back `null` when the row has no such figure (not collected, no ledger entry, a
 * pre-ledger sale after a refund) — a DIFFERENT claim from "zero", so this function refuses
 * to default it to 0 and leaves the caller to render "—" instead of a number that would read
 * as a real amount.
 */
const moneyCents = (cents, currency, lang, negative = false) => (cents == null ? null
  : <LedgerAmount cents={cents} currency={currency} lang={lang} negative={negative} figureCap={FIGURE_CAP} />);

/** The same text `LedgerAmount` renders, as a plain string for an interpolated caption. */
const moneyText = (cents, currency, lang) => `${formatMoneyLedger(cents, null, lang)} ${String(currency || "").trim().toUpperCase()}`.trim();

// A fact added to a cell: wrappable, so a caption never sets its column's width.
const SUBLINE = "text-[11px] text-slate-400 font-normal";

// The columns, as `ctx.*` heading keys, in order; header, rows, footer and every colSpan
// follow this list. Nine columns fit the 919px box a 1280px screen leaves once the page
// scrolls, in en/fr/es: money cells wrap between figure and currency code, the status chip
// may wrap, the refunded amount sits under the status it explains and the rail under the
// checkout reference, instead of each taking a column. A caption wraps only between words,
// so its longest word (fr "remboursement") still widens its column: with the refund
// captions the narrowest table measured 962 / 929 / 920px (fr / en / es) at 12px side
// padding, and 890 / 857 / 848px at 8px (2026-10-09, scripts/browser_qa/sa_d1_clienttx_fit.py).
const COLUMNS = ["colDate", "colTenant", "colKind", "colCollected", "colTax", "colCommission", "colOwed", "colStatus", "colCheckoutRef"];
const COLUMN_COUNT = COLUMNS.length;
const CELL = "px-2 py-3";
const HEAD = "px-2 py-4";

// Practice and Kind are word columns whose words the budget above cannot know in advance: a
// practice's name is the practitioner's own (an ordinary fr name with a 14-letter word widened
// Practice from 77 to 112px), and a kind label's longest word is product copy (the retired
// telehealth kind's 16-letter fr word widened Kind from 91 to 120px). Either alone pushed the fr table past its box. Their text
// therefore sits in a capped box, so neither can widen the table: a word of 12 letters or more
// is hyphenated where the browser has a dictionary for the page language (`<html lang>`
// follows the UI language, so a fr name read in en is not), and whatever is still wider than
// the cap breaks at it. Shorter words are never hyphenated, so an ordinary name or label is
// not cut to fill a line; Kind's cap holds the widest label word under 12 letters (en
// "Appointment", 84px). A cap applies at every width: the text never uses more than it, even
// when a wide screen leaves the column room. A hyphenated compound is one unbreakable unit
// (`KeepCompounds`; a hard hyphen is a break opportunity whatever `hyphens` says): measured in the
// browser (D6 L5, 2026-10-12) fr "Rendez-vous" is 84px and "Carte-cadeau" 88px, both inside the
// 5.5rem (88px) cap, so the cap stays. Keeping them whole raised the fr Kind column from 94 to
// 104px and the fr 1280 table from 936px to 945px in a 934px box, so the two caps are paid for by
// the checkout reference, which shows 5rem of its id (title and clipboard carry all of it; 6rem
// before D6, 929px at 5rem): with every cap reached at once and no hyphenation the fr table needs
// 911px (contract D1 section 16, a 919px box; the figures above are this harness's 934px box).
// "Carte-cadeau" fills the cap exactly, and a compound's width is a property of the font that
// renders it (the table draws in the OS's system font: in DejaVu Sans "Carte-cadeau" is 94.5px), so
// the cap cannot also be the compound's limit: Kind's box floors at its own min-content (`min-w-min`),
// and a compound wider than the cap widens its column instead of spilling into the next one. The
// floor never decides the fit (measured fr/en/es 1280, 2026-10-12, D1_FONT in the fit script): it
// costs the table 0px in this machine's system font (Noto Sans), Liberation Sans, Lato and FreeSans,
// and 7px in DejaVu Sans, where the fr table is 1011px without it; DejaVu's wider figures overflow
// the 934px box in en (938px) and es (953px) too, with no compound at all, so that is the nine-column
// budget's own dependence on the system font (D1 section 16), not Kind's. The floor is safe only with
// `overflow-wrap: anywhere`: unlike `break-word`, its emergency breaks count in min-content, so a
// long hyphen-free word (fr "Téléconsultation", 117px, where the browser has no fr dictionary)
// still breaks inside the cap instead of setting the floor. Rendering is otherwise the same:
// hyphenation, where available, is an ordinary break opportunity and comes before an emergency one.
const WORD_TEXT = "break-words hyphens-auto [hyphenate-limit-chars:12]";
const PRACTICE_TEXT = `max-w-[5rem] ${WORD_TEXT}`;
const KIND_TEXT = "max-w-[5.5rem] min-w-min [overflow-wrap:anywhere] hyphens-auto [hyphenate-limit-chars:12]";

// A footer total is the widest figure of its column. Its row's label already names the currency
// ("Total (CAD)"), so the total shows its figure alone: showing the code too forces a choice
// between a code free to wrap, which went under the figure even where the box had room (browser
// QA 2026-10-12, contract D1 section 19), and a code kept on the figure's line from a fixed box
// width up, which made the table scroll at 1366 as soon as a total reached eight digits (section
// 20). The code stays in the cell for assistive technology, out of the layout. The figure is
// capped like every other amount on the screen (FIGURE_CAP, section 22).
const totalFigure = (cents, currency, lang, negative) => (
  <>
    <LedgerFigure cents={cents} lang={lang} negative={negative} cap={FIGURE_CAP} />
    <span className="sr-only"> {String(currency || "").trim().toUpperCase()}</span>
  </>
);

// The money help, one short definition per line, in the contract's order (D1 section 5.5).
// Literal keys, so the i18n coverage scan sees every one.
const moneyHelpItems = (t) => [
  t("ctx.moneyHelp.collected"),
  t("ctx.moneyHelp.giftcard"),
  t("ctx.moneyHelp.refunded"),
  t("ctx.moneyHelp.owed"),
  t("ctx.moneyHelp.chargeback"),
  t("ctx.moneyHelp.source"),
];

// The screen opens on money actually collected; "All" (blank, never sent) and "Unpaid" stay one click away.
const INITIAL_FILTERS = { tenant_id: "", kind: "", payment_status: "paid", refund_status: "" };

export default function ClientTransactionsList() {
  const t = useT("superadmin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  // Display name of the picked practice, from the picker's own option — never sent to the
  // server, never derived from a row (an empty page has none).
  const [tenantLabel, setTenantLabel] = useState("");
  const [page, setPage] = useState(1);
  // The list on screen is a function of [filters, page] and nothing else.
  const { data, totalForCurrent, loading, loadFailed, retry } = usePagedList({
    path: "/super-admin/client-transactions", filters, page, setPage, pageSize: PAGE_SIZE,
  }); // data: { total, items }
  // The footer for the filters on screen: null while they load or after a failure.
  const totals = useFilteredRead({ path: TOTALS_PATH, filters, isPayload: isTotalsPayload });

  // A filter change always starts again at the first page.
  const applyFilter = (patch) => {
    setFilters((cur) => ({ ...cur, ...patch }));
    setPage(1);
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalsByCurrency = totals.data?.totals_by_currency || {};
  const currencies = Object.keys(totalsByCurrency);
  // Until the totals for these filters answer, or when they cannot, the footer says which.
  const totalsReady = totals.data !== null;
  const totalsTooLarge = !totals.loading && apiErrorCode(totals.error) === TOTALS_TIMEOUT;
  // What the footer adds up follows the payment filter on screen (blank = All).
  const totalsScope = filters.payment_status || "all";
  // Sales whose capture the ledger holds while their record says unpaid, under every filter but
  // the payment one (contract D1 section 4.5). The record decides what each payment filter
  // lists, so the default Paid view never shows them: this line is how it says so. Under All
  // and Unpaid they are among the listed unpaid checkouts, each with its own disagreement line.
  const ledgerCapturedUnpaid = totals.data?.ledger_captured_unpaid || 0;
  const ledgerCapturedKey = totalsScope === "paid" ? "ctx.totalsLedgerCaptured.hidden" : "ctx.totalsLedgerCaptured.listed";

  // Active filters, as the words the operator picked, in the order the contract fixes.
  const activeFilters = [
    filters.tenant_id && (tenantLabel || filters.tenant_id),
    filters.kind && t(`ctx.kind.${filters.kind}`),
    filters.payment_status && t(`ctx.paymentStatus.${filters.payment_status}`),
    filters.refund_status && t(`ctx.refundStatus.${filters.refund_status}`),
  ].filter(Boolean);
  const countText = totalForCurrent === null ? null
    : activeFilters.length
      ? t(pluralKey(lang, totalForCurrent, "ctx.countFiltered"), { n: totalForCurrent, filters: activeFilters.join(" · ") })
      : t(pluralKey(lang, totalForCurrent, "ctx.countAll"), { n: totalForCurrent });

  // A footer figure: the server's sum of that column over the whole filtered set. Rows whose
  // own value is unknown are excluded from the sum and counted, never silently read as zero.
  // `caption` names the figure inside the cell, for a column whose heading is not the figure's
  // (the refunded total sits under Status, beside the refund statuses it sums). `sublines` are
  // the totals of figures that have no column of their own and sit under the one they qualify
  // (the gift-card value spent beside Collected; chargebacks and the value returned to gift
  // cards beside Refunded): each is printed only when its sum is above zero.
  const totalCell = (cur, tot, field, key, { refund = false, caption = null, sublines = [] } = {}) => {
    const unknown = Number(tot.unknown?.[field] || 0);
    // Judged against the COLLECTED rows: an unpaid row adds nothing to any money sum, so it
    // must neither make a collected total read "—" nor count as a row with no figure.
    const collectedCount = Number(tot.collected_count || 0);
    const allUnknown = collectedCount === 0 || unknown >= collectedCount;
    const value = Number(tot[field] || 0);
    const text = allUnknown ? "—"
      : refund ? (value > 0 ? totalFigure(value, cur, lang, true) : "—")
        : totalFigure(value, cur, lang, false);
    const excluded = (n) => t(pluralKey(lang, n, "ctx.totalExcluded"), { n });
    return (
      <td className={`${CELL} align-top`} data-testid={`ctx-total-${key}-${cur}`}>
        {caption && <div className="text-[11px] uppercase tracking-wider text-slate-500">{caption}</div>}
        <span className={`tabular-nums ${refund && value > 0 ? "text-rose-600" : "font-semibold"}`}>{text}</span>
        {/* May wrap: a footnote must not set the column's width. */}
        {unknown > 0 && !allUnknown && <div className={SUBLINE}>{excluded(unknown)}</div>}
        {collectedCount > 0 && sublines.map((sub) => {
          const subValue = Number(tot[sub.field] || 0);
          const subUnknown = Number(tot.unknown?.[sub.field] || 0);
          return subValue > 0 && (
            <div key={sub.field} className={SUBLINE} data-testid={`ctx-total-${sub.key}-${cur}`}>
              {t(sub.label, { amount: moneyText(subValue, cur, lang) })}
              {subUnknown > 0 && <div>{excluded(subUnknown)}</div>}
            </div>
          );
        })}
      </td>
    );
  };

  return (
    <div data-testid="sa-client-transactions">
      <h1 className="font-sol font-semibold text-4xl mb-2">{t("ctx.title")}</h1>
      <div className="mb-6">
        <p className="text-slate-500">{t("ctx.subtitle")}</p>
        {/* Only a total read for the filters on screen is printed (not while new filters load, not after a failure). */}
        {countText !== null && <p className="text-slate-500 text-sm mt-1" data-testid="ctx-count">{countText}</p>}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <TenantPicker
          value={filters.tenant_id}
          onChange={(id, option) => {
            setTenantLabel(option ? tenantDisplayName(option) : "");
            applyFilter({ tenant_id: id });
          }}
          allowAll
          className="w-64"
          testId="ctx-filter-tenant"
        />

        <select
          value={filters.kind}
          onChange={(e) => applyFilter({ kind: e.target.value })}
          className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
          data-testid="ctx-filter-kind"
        >
          <option value="">{t("ctx.allKinds")}</option>
          {KIND_OPTIONS.map((k) => (
            <option key={k} value={k}>{t(`ctx.kind.${k}`)}</option>
          ))}
        </select>

        <div className="flex items-center gap-1.5" data-testid="ctx-filter-payment-status">
          <span className="text-xs text-slate-400 mr-1">{t("ctx.paymentStatusLabel")}</span>
          {["", ...PAYMENT_STATUS_OPTIONS].map((v) => (
            <button
              key={v || "all"}
              type="button"
              onClick={() => applyFilter({ payment_status: v })}
              aria-pressed={filters.payment_status === v}
              className={`px-2.5 py-1 rounded-full text-xs border transition ${
                filters.payment_status === v
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`ctx-ps-${v || "all"}`}
            >
              {v === "" ? t("ctx.all") : t(`ctx.paymentStatus.${v}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5" data-testid="ctx-filter-refund-status">
          <span className="text-xs text-slate-400 mr-1">{t("ctx.refundStatusLabel")}</span>
          {["", ...REFUND_STATUS_OPTIONS].map((v) => (
            <button
              key={v || "all"}
              type="button"
              onClick={() => applyFilter({ refund_status: v })}
              aria-pressed={filters.refund_status === v}
              className={`px-2.5 py-1 rounded-full text-xs border transition ${
                filters.refund_status === v
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`ctx-rs-${v || "all"}`}
            >
              {v === "" ? t("ctx.all") : t(`ctx.refundStatus.${v}`)}
            </button>
          ))}
        </div>
      </div>

      <LoadFailure
        message={loadFailed && !loading ? tc("errors.loadFailed") : ""}
        onRetry={retry}
        retryLabel={t("sal.retry")}
        testId="ctx-load-error"
        retryTestId="ctx-load-error-retry"
      />
      {loading && (loadFailed || !data) && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}

      {data && !loadFailed && (
      <>
      {/* The definitions are reference text, read once: closed, they cost one line above the
          table instead of a paragraph that pushed the first rows below the fold. */}
      <div className="mb-3 space-y-1 text-xs text-slate-500">
        <details data-testid="ctx-money-help">
          <summary className="cursor-pointer w-fit text-slate-600 hover:text-slate-800">{t("ctx.moneyHelpTitle")}</summary>
          <ul className="mt-1 mb-2 ml-4 list-disc space-y-0.5">
            {moneyHelpItems(t).map((line) => <li key={line}>{line}</li>)}
          </ul>
        </details>
        <p data-testid="ctx-ref-help">{t("ctx.refHelp")}</p>
      </div>
      <div
        className={`rounded-2xl border border-slate-200 overflow-hidden transition-opacity ${loading ? "opacity-60" : ""}`}
        aria-busy={loading}
        data-testid="ctx-table"
      >
        {/* Text cells (practice, kind, footnotes) and status chips wrap, amounts wrap only
            between figure and code; dates and references do not. Practice and Kind are capped,
            so no name or label can widen the table. What still does not fit (a box narrower
            than the 1280 budget) scrolls, with an edge cue saying so. */}
        <HorizontalScroller testId="ctx-table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider text-left">
            <tr>
              {COLUMNS.map((key) => <th key={key} className={HEAD}>{t(`ctx.${key}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((tx) => {
              const chip = statusChip({ ...tx, refund_obligation_state: tx.refund_obligation?.state }, t);
              const fc = tx.figures_currency || tx.currency;
              const src = tx.figures_source;
              const hasLedgerFigures = src === "ledger";
              // An order a gift card paid in full: nothing was charged, so no cash caption applies.
              const covered = src === "giftcard_covered" || src === "giftcard_covered_adjusted";
              const giftcard = Number(tx.giftcard_applied_cents || 0) > 0;
              const refunded = Number(tx.refunded_cents || 0) > 0;
              // Cash a bank took back through a dispute, and gift-card value returned to the card:
              // neither is a refund to the client, so each has its own line.
              const chargeback = Number(tx.chargeback_cents || 0) > 0;
              const cardReturned = Number(tx.giftcard_returned_cents || 0) > 0;
              const charged = tx.amount_cents == null ? null : moneyText(tx.amount_cents, tx.currency, lang);
              const otherCurrency = String(tx.currency || "").toLowerCase() !== String(fc || "").toLowerCase();
              const showCharged = charged !== null && !covered && (((refunded || chargeback) && src !== "not_collected") || (hasLedgerFigures && otherCurrency));
              const tax = moneyCents(tx.tax_cents, fc, lang);
              // A row with figures whose tax is null after money came back: the ledger (or
              // the pre-ledger record) does not split the tax out of what is left. Say which
              // event left it unrecorded; a tax that was simply never stamped is a bare dash.
              const cameBack = refunded || cardReturned;
              const taxUnrecorded = !NO_FIGURES.has(src) && tx.tax_cents == null && (cameBack || chargeback);
              const taxCaption = taxUnrecorded
                ? t(`ctx.taxNotRecorded.${cameBack && chargeback ? "refundAndChargeback" : chargeback ? "chargeback" : "refund"}`)
                : null;
              const commission = moneyCents(tx.commission_cents, fc, lang);
              const owed = moneyCents(tx.owed_to_practitioner_cents, fc, lang);
              const owedCaption = {
                checkout_record: "ctx.preLedgerRecord",
                pre_ledger_adjusted: "ctx.preLedgerAdjusted",
                giftcard_covered: "ctx.giftcardCoveredRecord",
                giftcard_covered_adjusted: "ctx.giftcardCoveredAdjusted",
                not_collected: "ctx.notCollected",
              }[src] || null;
              // A gift card's sale is owed to no practitioner yet, on the ledger and on a
              // pre-ledger record alike: its value is held until the card is spent.
              const giftcardHeld = tx.kind === "giftcard" && tx.owed_to_practitioner_cents != null;
              const refundCaption = (key) => hasLedgerFigures && refunded && <div className={SUBLINE}>{t(key)}</div>;
              const disagrees = tx.ledger_disagrees === "refund" || tx.ledger_disagrees === "capture" ? tx.ledger_disagrees : null;
              return (
                <tr key={tx.session_id} className="border-t border-slate-100 hover:bg-white" data-testid={`ctx-row-${tx.session_id}`} data-source={src}>
                  <td className={`${CELL} text-slate-500`}><DateTimeCell value={tx.created_at} lang={lang} /></td>
                  <td className={CELL}><div className={PRACTICE_TEXT}><PracticeCell name={tx._tenant_name} id={tx.tenant_id} /></div></td>
                  <td className={CELL} title={isKnownKind(tx.kind) ? undefined : (tx.kind || undefined)}>
                    <div className={KIND_TEXT}><KeepCompounds text={clientTxKindLabel(tx.kind, t)} /></div>
                  </td>
                  <td className={CELL} data-testid={`ctx-collected-${tx.session_id}`}>
                    {moneyCents(tx.collected_cents, fc, lang) ?? "—"}
                    {src === "not_collected" && charged !== null && (
                      <div className={SUBLINE}>{t("ctx.checkoutAmount", { amount: charged })}</div>
                    )}
                    {src === "ledger_unavailable" && <div className={SUBLINE}>{t("ctx.ledgerUnavailable")}</div>}
                    {src === "ledger_mixed_currency" && <div className={SUBLINE}>{t("ctx.ledgerMixedCurrency")}</div>}
                    {src === "capture_unbooked" && <div className={SUBLINE}>{t("ctx.captureUnbooked")}</div>}
                    {(src === "pre_ledger_adjusted" && refunded) && <div className={SUBLINE}>{t("ctx.afterRefund")}</div>}
                    {refundCaption("ctx.afterRefund")}
                    {chargeback && <div className={SUBLINE}>{t("ctx.afterChargeback")}</div>}
                    {giftcard && <div className={SUBLINE}>{t("ctx.giftcardApplied", { amount: moneyText(tx.giftcard_applied_cents, fc, lang) })}</div>}
                    {showCharged && <div className={SUBLINE}>{t("ctx.charged", { amount: charged })}</div>}
                  </td>
                  <td className={CELL} data-testid={`ctx-tax-${tx.session_id}`} title={taxCaption || undefined}>
                    {tax ?? "—"}
                    {taxCaption && <div className={SUBLINE}>{taxCaption}</div>}
                  </td>
                  <td className={CELL} data-testid={`ctx-commission-${tx.session_id}`}>
                    {commission ?? "—"}
                    {src === "not_collected" && <div className={SUBLINE}>{t("ctx.notCollected")}</div>}
                    {refundCaption("ctx.commissionKept")}
                  </td>
                  <td className={CELL} data-testid={`ctx-owed-${tx.session_id}`}>
                    {owed ?? "—"}
                    {refundCaption("ctx.owedAfterRefund")}
                    {hasLedgerFigures && chargeback && <div className={SUBLINE}>{t("ctx.owedAfterChargeback")}</div>}
                    {giftcardHeld && <div className={SUBLINE}>{t("ctx.giftcardHeld")}</div>}
                    {owedCaption && <div className={SUBLINE}>{t(owedCaption)}</div>}
                  </td>
                  {/* The cash refunded, the gift-card value returned and the chargeback sit under the
                      status they explain; an unknown or zero figure prints nothing (the chip
                      already says whether anything came back). */}
                  <td className={CELL} data-testid={`ctx-status-${tx.session_id}`}>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${chip.cls}`} title={chip.title}>{chip.label}</span>
                    {refunded && (
                      <div className="mt-1 text-rose-600" data-testid={`ctx-refunded-${tx.session_id}`}>
                        {moneyCents(tx.refunded_cents, fc, lang, true)}
                      </div>
                    )}
                    {cardReturned && (
                      <div className={`mt-1 ${SUBLINE}`} data-testid={`ctx-giftcard-returned-${tx.session_id}`}>
                        {t("ctx.giftcardReturned", { amount: moneyText(tx.giftcard_returned_cents, fc, lang) })}
                      </div>
                    )}
                    {chargeback && (
                      <div className="mt-1 text-rose-600" data-testid={`ctx-chargeback-${tx.session_id}`}>
                        {moneyCents(tx.chargeback_cents, fc, lang, true)}
                        <div className={SUBLINE}>{t("ctx.chargeback")}</div>
                      </div>
                    )}
                    {disagrees && (
                      <div className="mt-1 text-[11px] text-amber-700" data-testid={`ctx-disagrees-${tx.session_id}`}>
                        {t(`ctx.ledgerDisagrees.${disagrees}`)}
                      </div>
                    )}
                  </td>
                  <td className={CELL}>
                    <IdCell value={tx.session_id} width="max-w-[5rem]" />
                    {tx.rail && <div className="text-xs text-slate-400 whitespace-nowrap">{t("ctx.railLine", { rail: t(`ctx.rail.${tx.rail}`, undefined, tx.rail) })}</div>}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={COLUMN_COUNT} className="px-4 py-10 text-center text-slate-400" data-testid="ctx-empty">{t("ctx.empty")}</td>
              </tr>
            )}
          </tbody>
          {(currencies.length > 0 || !totalsReady || ledgerCapturedUnpaid > 0) && (
            <tfoot className="bg-slate-100 border-t-2 border-slate-300">
              <tr>
                <td
                  colSpan={COLUMN_COUNT}
                  className="px-4 py-2 text-[11px] text-slate-500 uppercase tracking-wider"
                  data-testid="ctx-totals-label"
                  data-scope={totalsScope}
                >
                  {t(`ctx.totalsLabel.${totalsScope}`)}
                </td>
              </tr>
              {ledgerCapturedUnpaid > 0 && (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className="px-4 py-2 text-xs text-amber-700"
                    data-testid="ctx-totals-ledger-captured"
                    data-scope={totalsScope}
                  >
                    {t(pluralKey(lang, ledgerCapturedUnpaid, ledgerCapturedKey), { n: ledgerCapturedUnpaid, unpaid: t("ctx.paymentStatus.unpaid") })}
                  </td>
                </tr>
              )}
              {!totalsReady && (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-3 text-sm text-slate-600" data-testid="ctx-totals-status">
                    {totals.loading || totalsTooLarge
                      ? <div role="status">{t(totals.loading ? "ctx.totalsLoading" : "ctx.totalsTooLarge")}</div>
                      : (
                        <LoadFailure
                          message={t("ctx.totalsFailed")}
                          onRetry={totals.retry}
                          retryLabel={t("sal.retry")}
                          testId="ctx-totals-error"
                          retryTestId="ctx-totals-retry"
                          className="p-3 rounded-lg"
                        />
                      )}
                  </td>
                </tr>
              )}
              {currencies.map((cur) => {
                const tot = totalsByCurrency[cur];
                const otherCcy = Number(tot.charged_in_other_currency || 0);
                const unpaid = Number(tot.not_collected?.count || 0);
                // Unpaid rows with no recorded amount add nothing to the sum: never print it as 0.
                const unpaidNoAmount = Number(tot.not_collected?.unknown_amount || 0);
                return (
                  <tr key={cur} data-testid={`ctx-total-${cur}`}>
                    <td colSpan={3} className={`${CELL} align-top`} data-testid={`ctx-total-label-${cur}`}>
                      <div className="font-semibold text-slate-700 whitespace-nowrap">{t("ctx.totalLabel", { cur: cur.toUpperCase() })}</div>
                      <div className="text-xs text-slate-500 whitespace-nowrap">{t(pluralKey(lang, tot.count, "ctx.totalCount"), { n: tot.count })}</div>
                      {otherCcy > 0 && (
                        <div className={SUBLINE} data-testid={`ctx-total-other-ccy-${cur}`}>
                          {t(pluralKey(lang, otherCcy, "ctx.totalOtherCurrency"), { n: otherCcy })}
                        </div>
                      )}
                      {unpaid > 0 && (
                        <div className={SUBLINE} data-testid={`ctx-total-not-collected-${cur}`}>
                          {unpaidNoAmount >= unpaid
                            ? t(pluralKey(lang, unpaid, "ctx.totalNotCollectedNoAmount"), { n: unpaid })
                            : t(pluralKey(lang, unpaid, "ctx.totalNotCollected"), { n: unpaid, amount: moneyText(tot.not_collected.amount_cents, cur, lang) })}
                          {unpaidNoAmount > 0 && unpaidNoAmount < unpaid && (
                            <div>{t(pluralKey(lang, unpaidNoAmount, "ctx.totalExcluded"), { n: unpaidNoAmount })}</div>
                          )}
                        </div>
                      )}
                    </td>
                    {totalCell(cur, tot, "collected_cents", "collected", {
                      sublines: [{ field: "giftcard_applied_cents", key: "giftcard", label: "ctx.giftcardApplied" }],
                    })}
                    {totalCell(cur, tot, "tax_cents", "tax")}
                    {totalCell(cur, tot, "commission_cents", "commission")}
                    {totalCell(cur, tot, "owed_to_practitioner_cents", "owed")}
                    {totalCell(cur, tot, "refunded_cents", "refunded", {
                      refund: true,
                      caption: t("ctx.colRefunded"),
                      sublines: [
                        { field: "chargeback_cents", key: "chargeback", label: "ctx.totalChargeback" },
                        { field: "giftcard_returned_cents", key: "giftcard-returned", label: "ctx.totalGiftcardReturned" },
                      ],
                    })}
                    <td className={CELL} />
                  </tr>
                );
              })}
            </tfoot>
          )}
        </table>
        </HorizontalScroller>
        {total > PAGE_SIZE && (
          <Pager
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            shown={items.length}
            onPage={setPage}
            infoText={(v) => t("sal.pagerInfo", v)}
            prevLabel={t("sal.pagerPrev")}
            nextLabel={t("sal.pagerNext")}
            testId="ctx-pager"
            tone="slate"
          />
        )}
      </div>
      </>
      )}
    </div>
  );
}
